import { cache } from '../utils/cache'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api'

interface ApiOptions extends RequestInit {
  token?: string
  useCache?: boolean
}

async function fetchWithAuth(url: string, options: ApiOptions = {}) {
  const { token, useCache = false, ...fetchOptions } = options

  // Check cache first
  if (useCache) {
    const cacheKey = `api:${url}`
    const cachedData = cache.get(cacheKey)
    if (cachedData) {
      console.log(`Cache hit for: ${url}`)
      return cachedData
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Merge in any additional headers from options
  if (fetchOptions.headers) {
    const optHeaders = fetchOptions.headers as Record<string, string>
    Object.assign(headers, optHeaders)
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...fetchOptions,
    headers,
  })

  // Handle empty responses (like 204 No Content or empty error responses)
  let data = null
  const contentType = response.headers.get('content-type')
  const hasJsonContent = contentType && contentType.includes('application/json')

  if (hasJsonContent) {
    const text = await response.text()
    if (text) {
      try {
        data = JSON.parse(text)
      } catch {
        // If JSON parsing fails, data remains null
      }
    }
  }

  // For 409 Conflict (sync in progress), return the response body so UI can show friendly message
  if (response.status === 409) {
    return data
  }

  if (!response.ok) {
    // Use detailed error message from API response if available
    const errorMessage = data?.details || data?.error || response.statusText || `HTTP ${response.status}`
    throw new Error(errorMessage)
  }

  // Cache the response
  if (useCache) {
    const cacheKey = `api:${url}`
    cache.set(cacheKey, data)
    console.log(`Cached: ${url}`)
  }

  return data
}

export const api = {
  // User/Role info
  getCurrentUser: (token: string) => fetchWithAuth('/me', { token }),

  getDashboardStats: (token: string) => fetchWithAuth('/dashboard/stats', { token, useCache: true }),
  getCustomers: (token: string, filter: 'all' | 'withProducts' = 'withProducts') => {
    const url = `/customers?filter=${filter}`
    return fetchWithAuth(url, { token, useCache: true })
  },
  getLicenses: (token: string, customerId?: string) => {
    const url = customerId ? `/licenses?customerId=${customerId}` : '/licenses'
    return fetchWithAuth(url, { token, useCache: true })
  },
  getAzureUsage: (token: string, params?: { customerId?: string; startDate?: string; endDate?: string; view?: string }) => {
    let url = '/azure-usage'
    const queryParams = new URLSearchParams()

    if (params?.customerId) queryParams.append('customerId', params.customerId)
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.view) queryParams.append('view', params.view)

    const queryString = queryParams.toString()
    if (queryString) url += `?${queryString}`

    return fetchWithAuth(url, { token })
  },
  getAzureUsageSummary: (token: string, params?: { customerId?: string; startDate?: string; endDate?: string }) => {
    return api.getAzureUsage(token, { ...params, view: 'summary' })
  },
  getAzureUsageByCustomer: (token: string, params?: { startDate?: string; endDate?: string }) => {
    return api.getAzureUsage(token, { ...params, view: 'byCustomer' })
  },
  getAzureUsageByResourceType: (token: string, params?: { customerId?: string; startDate?: string; endDate?: string }) => {
    return api.getAzureUsage(token, { ...params, view: 'byResourceType' })
  },
  getAzureUsageTrends: (token: string, params?: { customerId?: string; startDate?: string; endDate?: string }) => {
    return api.getAzureUsage(token, { ...params, view: 'trends' })
  },
  syncLicenseData: (token: string) => fetchWithAuth('/sync-license-data', { method: 'POST', token }),
  syncPartnerCenter: (token: string) => fetchWithAuth('/sync-partner-center', { method: 'POST', token }),
  syncAzureUsage: (token: string) => fetchWithAuth('/azure-usage', { method: 'POST', token }),

  // Azure Billing (unbilled and billed)
  getAzureBilling: (token: string, params?: { view?: 'unbilled' | 'billed' | 'both'; customerId?: string; invoiceId?: string }) => {
    let url = '/azure-billing'
    const queryParams = new URLSearchParams()

    if (params?.view) queryParams.append('view', params.view)
    if (params?.customerId) queryParams.append('customerId', params.customerId)
    if (params?.invoiceId) queryParams.append('invoiceId', params.invoiceId)

    const queryString = queryParams.toString()
    if (queryString) url += `?${queryString}`

    return fetchWithAuth(url, { token })
  },
  syncBillingData: (token: string, params?: { invoiceId?: string; monthsBack?: number }) => {
    let url = '/sync-billing'
    const queryParams = new URLSearchParams()

    if (params?.invoiceId) queryParams.append('invoiceId', params.invoiceId)
    if (params?.monthsBack) queryParams.append('monthsBack', params.monthsBack.toString())

    const queryString = queryParams.toString()
    if (queryString) url += `?${queryString}`

    return fetchWithAuth(url, { method: 'POST', token })
  },
  syncUnbilledUsage: (token: string) =>
    fetchWithAuth('/sync-unbilled-usage', { method: 'POST', token }),
  getUnbilledSyncStatus: (token: string) =>
    fetchWithAuth('/unbilled-sync-status', { token }),

  // Setup - Customers
  getSetupCustomers: (token: string, params?: { status?: string; search?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)
    const query = queryParams.toString()
    return fetchWithAuth(`/setup/customers${query ? `?${query}` : ''}`, { token })
  },
  getSetupCustomer: (token: string, customerId: string) =>
    fetchWithAuth(`/setup/customers/${customerId}`, { token }),
  createSetupCustomer: (token: string, data: { name: string; domain?: string; status?: string; primaryContact?: string; primaryEmail?: string; phone?: string; notes?: string }) =>
    fetchWithAuth('/setup/customers', { method: 'POST', token, body: JSON.stringify(data) }),
  updateSetupCustomer: (token: string, customerId: string, data: { name?: string; domain?: string; status?: string; primaryContact?: string; primaryEmail?: string; phone?: string; notes?: string }) =>
    fetchWithAuth(`/setup/customers/${customerId}`, { method: 'PUT', token, body: JSON.stringify(data) }),
  deleteSetupCustomer: (token: string, customerId: string) =>
    fetchWithAuth(`/setup/customers/${customerId}`, { method: 'DELETE', token }),

  // Setup - Customer Mappings
  createCustomerMapping: (token: string, customerId: string, data: { integrationId: string; externalId: string; externalName?: string }) =>
    fetchWithAuth(`/setup/customers/${customerId}/mappings`, { method: 'POST', token, body: JSON.stringify(data) }),
  deleteCustomerMapping: (token: string, customerId: string, integrationId: string) =>
    fetchWithAuth(`/setup/customers/${customerId}/mappings/${integrationId}`, { method: 'DELETE', token }),

  // Setup - Import
  importCustomers: (token: string, data: { integrationId: string; externalIds: string[] }) =>
    fetchWithAuth('/setup/customers/import', { method: 'POST', token, body: JSON.stringify(data) }),

  // Setup - Sync metrics
  syncAllCustomerMetrics: (token: string) =>
    fetchWithAuth('/setup/customers/sync', { method: 'POST', token }),
  syncCustomerMetrics: (token: string, customerId: string) =>
    fetchWithAuth(`/setup/customers/${customerId}/sync`, { method: 'POST', token }),

  // Setup - Integrations
  getSetupIntegrations: (token: string) =>
    fetchWithAuth('/setup/integrations', { token }),
  getSetupIntegration: (token: string, integrationId: string) =>
    fetchWithAuth(`/setup/integrations/${integrationId}`, { token }),
  updateSetupIntegration: (token: string, integrationId: string, data: { isEnabled?: boolean; syncFrequencyHours?: number }) =>
    fetchWithAuth(`/setup/integrations/${integrationId}`, { method: 'PUT', token, body: JSON.stringify(data) }),
  testIntegrationConnection: (token: string, integrationId: string) =>
    fetchWithAuth(`/setup/integrations/${integrationId}/test`, { method: 'POST', token }),
  getExternalCustomers: (token: string, integrationId: string) =>
    fetchWithAuth(`/setup/integrations/${integrationId}/external-customers`, { token }),

  // M365 User Management
  // Get customer tenant ID for authentication (use partner tenant token)
  getM365TenantId: (token: string, customerId: string) =>
    fetchWithAuth(`/m365/tenant?customerId=${customerId}`, { token }),
  // Get users (use customer tenant token)
  getM365Users: (token: string, customerId: string, params?: { top?: number; search?: string; filter?: string }) => {
    const queryParams = new URLSearchParams()
    queryParams.append('customerId', customerId)
    if (params?.top) queryParams.append('top', params.top.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.filter) queryParams.append('filter', params.filter)
    return fetchWithAuth(`/m365/users?${queryParams.toString()}`, { token })
  },
  getM365User: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}?customerId=${customerId}`, { token }),
  updateM365User: (token: string, customerId: string, userId: string, data: {
    displayName?: string
    givenName?: string
    surname?: string
    jobTitle?: string
    department?: string
    officeLocation?: string
    mobilePhone?: string
    usageLocation?: string
  }) =>
    fetchWithAuth(`/m365/users/${userId}?customerId=${customerId}`, { method: 'PATCH', token, body: JSON.stringify(data) }),
  resetM365UserPassword: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/resetPassword?customerId=${customerId}`, { method: 'POST', token }),
  forceM365PasswordChange: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/forcePasswordChange?customerId=${customerId}`, { method: 'POST', token }),
  setM365UserStatus: (token: string, customerId: string, userId: string, enabled: boolean) =>
    fetchWithAuth(`/m365/users/${userId}/status?customerId=${customerId}`, { method: 'POST', token, body: JSON.stringify({ enabled }) }),
  revokeM365UserSessions: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/revokeSignInSessions?customerId=${customerId}`, { method: 'POST', token }),
  assignM365UserLicense: (token: string, customerId: string, userId: string, data: {
    addLicenses?: Array<{ skuId: string; disabledPlans?: string[] }>
    removeLicenses?: string[]
  }) =>
    fetchWithAuth(`/m365/users/${userId}/assignLicense?customerId=${customerId}`, { method: 'POST', token, body: JSON.stringify(data) }),
  getM365AvailableLicenses: (token: string, customerId: string) =>
    fetchWithAuth(`/m365/licenses?customerId=${customerId}`, { token }),
  // Authorize the Partner License Portal app in a customer tenant (creates service principal)
  authorizeAppInCustomer: (token: string, customerTenantId: string) =>
    fetchWithAuth(`/m365/authorize-app?customerTenantId=${customerTenantId}`, { method: 'POST', token }),

  // M365 User Detail - Groups, Devices, Sign-in logs
  getM365UserGroups: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/groups?customerId=${customerId}`, { token }),
  getM365UserDevices: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/devices?customerId=${customerId}`, { token }),
  getM365UserSignIns: (token: string, customerId: string, userId: string, params?: { top?: number }) => {
    const queryParams = new URLSearchParams()
    queryParams.append('customerId', customerId)
    if (params?.top) queryParams.append('top', params.top.toString())
    return fetchWithAuth(`/m365/users/${userId}/signins?${queryParams.toString()}`, { token })
  },
  getM365UserMailbox: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/mailbox?customerId=${customerId}`, { token }),
  getM365UserOneDrive: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/onedrive?customerId=${customerId}`, { token }),
  // Mailbox usage from Reports API (storage info)
  getM365MailboxUsage: (token: string, customerId: string, userPrincipalName?: string, displayName?: string) => {
    const queryParams = new URLSearchParams()
    queryParams.append('customerId', customerId)
    if (userPrincipalName) queryParams.append('userPrincipalName', userPrincipalName)
    if (displayName) queryParams.append('displayName', displayName)
    return fetchWithAuth(`/m365/mailbox-usage?${queryParams.toString()}`, { token })
  },

  // M365 User Authentication Methods
  getM365UserAuthMethods: (token: string, customerId: string, userId: string) =>
    fetchWithAuth(`/m365/users/${userId}/auth-methods?customerId=${customerId}`, { token }),
  deleteM365UserAuthMethod: (token: string, customerId: string, userId: string, methodType: string, methodId: string) =>
    fetchWithAuth(`/m365/users/${userId}/auth-methods/${methodType}/${methodId}?customerId=${customerId}`, { method: 'DELETE', token }),
  createM365UserTAP: (token: string, customerId: string, userId: string, options?: { lifetimeInMinutes?: number; isUsableOnce?: boolean }) =>
    fetchWithAuth(`/m365/users/${userId}/auth-methods/tap?customerId=${customerId}`, {
      method: 'POST',
      token,
      body: options ? JSON.stringify(options) : undefined,
    }),
  addM365UserPhoneMethod: (token: string, customerId: string, userId: string, data: { phoneNumber: string; phoneType: 'mobile' | 'alternateMobile' | 'office' }) =>
    fetchWithAuth(`/m365/users/${userId}/auth-methods/phone?customerId=${customerId}`, {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
  updateM365UserPhoneMethod: (token: string, customerId: string, userId: string, methodId: string, data: { phoneNumber: string; phoneType: 'mobile' | 'alternateMobile' | 'office' }) =>
    fetchWithAuth(`/m365/users/${userId}/auth-methods/phone/${methodId}?customerId=${customerId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),
  addM365UserEmailMethod: (token: string, customerId: string, userId: string, emailAddress: string) =>
    fetchWithAuth(`/m365/users/${userId}/auth-methods/email?customerId=${customerId}`, {
      method: 'POST',
      token,
      body: JSON.stringify({ emailAddress }),
    }),

  // Audit Logs
  getAuditLogs: (token: string, params?: {
    startDate?: string
    endDate?: string
    customerId?: string
    performedBy?: string
    actionType?: string
    actionCategory?: string
    status?: string
    page?: number
    pageSize?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.customerId) queryParams.append('customerId', params.customerId)
    if (params?.performedBy) queryParams.append('performedBy', params.performedBy)
    if (params?.actionType) queryParams.append('actionType', params.actionType)
    if (params?.actionCategory) queryParams.append('actionCategory', params.actionCategory)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString())
    const query = queryParams.toString()
    return fetchWithAuth(`/audit-logs${query ? `?${query}` : ''}`, { token })
  },
  getAuditLogFilters: (token: string) =>
    fetchWithAuth('/audit-logs/filters', { token }),
}
